/**
 * StockValidationModal
 *
 * Shown when useStockValidation() finds items whose cart qty
 * exceeds live stock (or are fully out-of-stock).
 *
 * ✅ Purely presentational – no cart mutation logic inside.
 *    Pass `onRemoveItem` and `onClose` from the parent (CartPage).
 *    CartPage already has removeProduct / decreaseQty – just wire those up.
 */

import { AlertTriangle, PackageX, ShoppingCart, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * @param {Object}   props
 * @param {import('./useStockValidation').StockIssue[]} props.issues
 * @param {boolean}  props.isINR          – for size display (US vs IN)
 * @param {Function} props.onRemoveItem   – called with (issue) to let CartPage remove it
 * @param {Function} props.onClose        – dismiss without action
 * @param {Function} props.onProceed      – called if user wants to continue after fixing issues
 *                                          (enabled only when issues are cleared)
 */
export default function StockValidationModal({
  issues = [],
  isINR = false,
  onRemoveItem,
  onClose,
  onProceed,
}) {
  if (!issues.length) return null;

  const outOfStockIssues  = issues.filter((i) => i.type === 'out_of_stock');
  const exceedsStockIssues = issues.filter((i) => i.type === 'exceeds_stock');

  /** Human-readable size label */
  const sizeLabel = (issue) => {
    const size = isINR ? issue.ring_inr_size : issue.ring_size;
    if (!size) return null;
    return `Size ${size}`;
  };

  return (
    /* Overlay */
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">

        {/* ── Header ── */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
       
            <div>
              <h2 className="text-lg font-semibold text-[#1a1d29]">
                Stock Updated
              </h2>
              <p className="text-sm text-[#6b7280] mt-0.5">
                Some items in your cart are no longer available in the requested quantity.Please remove them or adjust the quantity.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-[#9ca3af] hover:text-[#64483c] transition-colors flex-shrink-0 ml-4"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Issue list ── */}
        <div className="overflow-y-auto flex-1 p-6 space-y-3">

          {/* Out of stock */}
          {outOfStockIssues.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-red-500 mb-2 flex items-center gap-1.5">
                <PackageX className="h-3.5 w-3.5" />
                Out of Stock
              </p>
              <div className="space-y-2">
                {outOfStockIssues.map((issue, idx) => (
                  <IssueRow
                    key={idx}
                    issue={issue}
                    sizeLabel={sizeLabel(issue)}
                    onRemove={() => onRemoveItem(issue)}
                    variant="out_of_stock"
                  />
                ))}
              </div>
            </div>
          )}

          {/* Exceeds stock */}
          {exceedsStockIssues.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-2 flex items-center gap-1.5">
                <ShoppingCart className="h-3.5 w-3.5" />
                Quantity Exceeds Available Stock
              </p>
              <div className="space-y-2">
                {exceedsStockIssues.map((issue, idx) => (
                  <IssueRow
                    key={idx}
                    issue={issue}
                    sizeLabel={sizeLabel(issue)}
                    onRemove={() => onRemoveItem(issue)}
                    variant="exceeds_stock"
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="p-6 pt-4 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 py-5 text-[#64483c] border-[#e5e7eb] hover:bg-[#f8f9fa] rounded-lg"
          >
            Review Cart
          </Button>
          {/* Proceed button – only enabled when all issues are cleared */}
          {issues.length === 0 && (
            <Button
              onClick={onProceed}
              className="flex-1 bg-[#64483c] hover:bg-[#a8704e] text-white py-5 rounded-lg flex items-center justify-center gap-2"
            >
              Proceed to Checkout
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-component: single issue row ─────────────────────────────────────────
function IssueRow({ issue, sizeLabel, onRemove, variant }) {
  const isOutOfStock = variant === 'out_of_stock';

  return (
    <div
      className={`flex items-start justify-between gap-4 p-3 rounded-xl border ${
        isOutOfStock
          ? 'bg-red-50 border-red-100'
          : 'bg-amber-50 border-amber-100'
      }`}
    >
      <div className="flex-1 min-w-0">
        {/* Product name */}
        <p className="text-sm font-medium text-[#1a1d29] truncate">
          {issue.productName}
        </p>

        {/* Attributes */}
        <div className="flex flex-wrap gap-1.5 mt-1">
          {sizeLabel && (
            <span className="text-xs px-2 py-0.5 rounded bg-white/70 text-[#64483c] font-medium border border-[#e5e7eb]">
              {sizeLabel}
            </span>
          )}
          {issue.plating_name && (
            <span className="text-xs px-2 py-0.5 rounded bg-white/70 text-[#64483c] font-medium border border-[#e5e7eb]">
              {issue.plating_name}
            </span>
          )}
        </div>

        {/* Stock message */}
        <p className={`text-xs mt-1.5 font-medium ${
          isOutOfStock ? 'text-red-600' : 'text-amber-700'
        }`}>
          {isOutOfStock
            ? 'No longer in stock'
            : `You have ${issue.cartQty} in cart — only ${issue.availableQty} available`}
        </p>
      </div>

      {/* Remove button */}
      {/* <button
        onClick={onRemove}
        className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap flex-shrink-0 ${
          isOutOfStock
            ? 'bg-red-100 text-red-700 hover:bg-red-200'
            : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
        }`}
      >
        Remove
      </button> */}
    </div>
  );
}